import { Fragment } from 'react';
import { Pencil, Trash2, Unplug } from 'lucide-react';
import type {
  RoutingBreakerRecord,
  RoutingSkuTargetRecord,
  TargetProbeLayer,
  TargetProbeStateRecord,
} from '@nadoutong/contracts';
import type { LegSkuArming } from '../guaranteed/legModel.ts';
import type { TargetLegs } from './probeDot.ts';
import { ProbeStatusDot } from './ProbeStatusDot.tsx';
import { TryProbeButton } from './TryProbeButton.tsx';
import { disabledDetail } from './disabledNote.ts';
import { formatLocalTimestamp } from '../../components/formatters.ts';
import { LIVE_CAPABILITY_STATUSES, capabilityStatusLabel } from '../capabilityStatus.ts';
import { useT } from '../../i18n/index.ts';
import { Button, Chip, IconButton, Td, Tooltip, Tr } from '../../ui/index.ts';
import { BreakerStateChip } from '../RoutingBreakersDrawer.tsx';
import {
  joinIdentitySegments,
  skuTargetIdentifiers,
  targetIdentitySegments,
} from '../tenantRoutingShared.ts';
import type { TargetShare } from './firstAttemptShare.ts';

/**
 * One row of the selected SKU's target pool (#1494).
 *
 * Before this split the row printed five opaque ids on its face — 206
 * characters over three lines inside a 388px column — of which `ups_`, `ua_`
 * and `ukb_` were byte-identical on every row of a namespace. The only parts
 * that differed between rows, the upstream model and the capability, were the
 * hardest to find. That row was 101px tall and its intrinsic width squeezed
 * 「权重」and「重试顺序」down to 47px and 44px, which stacked their headers one
 * character per line.
 *
 * The face now carries only what an operator reads to tell rows apart —
 * upstream model, protocol, and a short form of each identity — and the full
 * ids move into a native `<details>`, collapsed. `<details>` rather than a
 * hand-rolled toggle: keyboard reachability, `aria-expanded` and the
 * open/closed announcement come from the element.
 */

/**
 * `→ <upstream model> · <protocol>` — the half of the SKU mapping that varies
 * per row. The left-hand side (this SKU's canonical name) is the panel header
 * directly above, identical on every row, so it is not reprinted here.
 *
 * Both halves are nullable on the wire even when a capability is pinned, so
 * the line is assembled from whichever parts exist rather than interpolated.
 */
function upstreamMappingLine(target: RoutingSkuTargetRecord): string {
  // `typeof === 'string'` rather than `!== null` guards against FIXTURE drift,
  // not against the wire. No server omits these: the contract marks them
  // required and `rowToSkuTargetRecord` assigns every field explicitly, so an
  // unpinned row sends `null`. But the ops rig types canned bodies as
  // `unknown[]`, so a hand-written fixture that drops the keys type-checks
  // fine and then makes them `undefined` — which a strict null check reads as
  // "present" before throwing on `.length`. Cheap to be total here; see
  // `test/routing-target-fixture.ts` for the compile-time half of the fix.
  const parts = [target.upstream_model_name, target.capability_protocol]
    .filter((part): part is string => typeof part === 'string' && part.length > 0);
  return `→ ${parts.join(' · ')}`;
}

interface Props {
  readonly target: RoutingSkuTargetRecord;
  /** This target's breaker entry, or `null` when it is not tripped. */
  readonly breaker: RoutingBreakerRecord | null;
  /** Probe state for this target row, or `null` when never probed. */
  readonly probe: TargetProbeStateRecord | null;
  /** Physical-leg state by region, mapped once per page by target id. */
  readonly probeLegs: TargetLegs | null;
  /** Whether this SKU's guaranteed-center mechanisms can actually demote a leg. */
  readonly arming: LegSkuArming;
  /** Routing layer of this pool's rows, for the 试打 shot. */
  readonly probeLayer: TargetProbeLayer;
  /** This target's first-attempt share, or `null` if the pool did not size it. */
  readonly share: TargetShare | null;
  readonly deleting: boolean;
  readonly resettingBreaker: boolean;
  readonly onEdit: (target: RoutingSkuTargetRecord) => void;
  readonly onDelete: (target: RoutingSkuTargetRecord) => void;
  readonly onBreakerReset: (record: RoutingBreakerRecord) => void;
  readonly onBreakerTrip: (target: RoutingSkuTargetRecord) => void;
}

export function TenantRoutingSkuPoolRow({
  target,
  breaker,
  probe,
  probeLegs,
  arming,
  probeLayer,
  share,
  deleting,
  resettingBreaker,
  onEdit,
  onDelete,
  onBreakerReset,
  onBreakerTrip,
}: Props): JSX.Element {
  const t = useT();
  const mapping = upstreamMappingLine(target);
  const shortIdentity = joinIdentitySegments(targetIdentitySegments(target));

  return (
    <Tr data-testid={`tenant-routing-pool-row-${target.id}`}>
      <Td mono className="align-top">
        <span className="block truncate text-paper" title={target.upstream_channel_slug}>
          {target.upstream_channel_slug}
        </span>
      </Td>
      <Td className="align-top">
        <div className="flex min-w-0 flex-col gap-1">
          {/*
            `== null`, not `=== null`: a legacy unpinned row sends an explicit
            `null` here, and the loose check additionally absorbs a fixture
            that omitted the key. Both spellings mean "no capability pinned".
          */}
          {target.capability_id == null ? (
            <Chip tone="warn" lamp={false}>{t('tenant_routing.target.capability_legacy')}</Chip>
          ) : (
            <span
              className="block truncate font-mono text-paper"
              title={mapping}
              data-testid={`tenant-routing-pool-capability-${target.id}`}
            >
              {mapping}
            </span>
          )}
          {target.capability_id != null && !LIVE_CAPABILITY_STATUSES.has(target.capability_status ?? '') ? (
            <span className="block truncate text-micro text-warn">
              {capabilityStatusLabel(target.capability_status, t)}
            </span>
          ) : null}
          <details className="min-w-0" data-testid={`tenant-routing-pool-identity-${target.id}`}>
            {/*
              The short forms are literal suffixes of the ids listed below, so
              the collapsed row and the expanded one can be matched by eye —
              see `shortIdentifier`.
            */}
            {/*
              `title` carries the content, matching the slug and mapping lines
              above: this line also `truncate`s, and the short-id triple can
              overrun the column, so a static label would leave the clipped
              tail unreadable with no tooltip to recover it.
            */}
            <summary
              className="cursor-pointer truncate font-mono text-micro text-paper-low hover:text-paper-mid"
              title={shortIdentity}
            >
              {shortIdentity}
            </summary>
            <dl className="m-0 mt-1 grid grid-cols-[auto_minmax(0,1fr)] gap-x-2 gap-y-0.5">
              {skuTargetIdentifiers(target).map((identifier) => (
                <Fragment key={identifier.labelKey}>
                  <dt className="text-micro text-paper-low">{t(identifier.labelKey)}</dt>
                  {/*
                    `break-all` rather than `truncate`: this is the one place
                    the full id has to be readable, and a longer id must spend
                    another line rather than another pixel of column width.
                  */}
                  <dd className="m-0 min-w-0 break-all font-mono text-micro text-paper-mid">
                    {identifier.value}
                  </dd>
                </Fragment>
              ))}
            </dl>
          </details>
        </div>
      </Td>
      <Td num className="align-top">
        <div className="flex flex-col items-end gap-1">
          <span className="inline-flex items-center gap-1.5">
            {target.weight === 0 ? (
              <Chip tone="neutral" lamp={false}>{t('tenant_routing.pool.reserve')}</Chip>
            ) : null}
            {target.weight}
          </span>
          {/*
            The raw weight alone never answered "how much of the traffic is
            that" — it only means something against the pool's total, which is
            why the share arrives as a prop rather than being derived here
            (#1495). Only `active` targets get a bar: a reserve target already
            says 「纯兜底」 above, and drawing it an empty 0% bar would imply it
            is in the running for first attempts and simply losing.
          */}
          {share !== null && share.kind === 'active' ? (
            <div
              className="flex w-full flex-col items-end gap-0.5"
              data-testid={`tenant-routing-pool-share-row-${target.id}`}
              title={t('tenant_routing.pool.share_label', {
                channel: target.upstream_channel_slug,
                percent: share.percent,
              })}
            >
              <div className="h-1 w-full overflow-hidden rounded-full bg-line-soft" aria-hidden="true">
                <div className="h-full rounded-full bg-signal" style={{ width: `${String(share.percent)}%` }} />
              </div>
              <span className="font-mono text-micro text-paper">{share.percent}%</span>
            </div>
          ) : null}
        </div>
      </Td>
      <Td num className="align-top">
        {target.retry_order === null
          ? <span className="text-paper-low">{t('tenant_routing.pool.retry_order_auto')}</span>
          : target.retry_order}
      </Td>
      <Td className="align-top">
        <span
          className="inline-flex flex-wrap items-center gap-1.5"
          data-testid={`tenant-routing-pool-status-${target.id}`}
        >
          <ProbeStatusDot targetId={target.id} legs={probeLegs} arming={arming} />
          {target.enabled ? (
            <Chip tone="ok">{t('tenant_routing.status.target_enabled')}</Chip>
          ) : (
            <DisabledTargetChip target={target} onEdit={onEdit} />
          )}
          {breaker !== null ? <BreakerStateChip record={breaker} /> : null}
          {probe !== null ? (
            <Chip
              tone={probe.tripped ? 'bad' : 'ok'}
              lamp={false}
              testId={`tenant-routing-probe-${target.id}`}
              title={[
                probe.next_probe_at === null
                  ? null
                  : t('tenant_routing.probe.next', { at: formatLocalTimestamp(probe.next_probe_at) }),
                probe.backoff_level > 0
                  ? t('tenant_routing.probe.backoff', { level: String(probe.backoff_level) })
                  : null,
              ].filter((part): part is string => part !== null).join(' · ') || undefined}
            >
              {probe.tripped
                ? t('tenant_routing.probe.tripped')
                : t('tenant_routing.probe.ok', {
                  at: probe.last_probe_at === null
                    ? '—'
                    : formatLocalTimestamp(probe.last_probe_at),
                })}
            </Chip>
          ) : null}
        </span>
      </Td>
      <Td className="align-top text-right">
        <span className="inline-flex items-center gap-1">
          <TryProbeButton layer={probeLayer} targetId={target.id} />
          {breaker !== null ? (
            <Button
              size="sm"
              disabled={resettingBreaker}
              onClick={() => onBreakerReset(breaker)}
              data-testid={`tenant-routing-breaker-reset-${target.id}`}
            >
              {t('tenant_routing.breaker.reset')}
            </Button>
          ) : null}
          {breaker === null || breaker.state !== 'forced' ? (
            // Force-out stays reachable for healthy targets (the maintenance
            // use case) and for organically open ones (converts to forced /
            // extends). Forced entries only offer recovery.
            <IconButton
              size="sm"
              label={t('tenant_routing.breaker.trip')}
              onClick={() => onBreakerTrip(target)}
              data-testid={`tenant-routing-breaker-trip-${target.id}`}
            >
              <Unplug aria-hidden="true" className="h-3.5 w-3.5" />
            </IconButton>
          ) : null}
          <IconButton
            size="sm"
            label={t('tenant_routing.action.edit_default')}
            onClick={() => onEdit(target)}
            data-testid={`tenant-routing-target-edit-${target.id}`}
          >
            <Pencil aria-hidden="true" className="h-3.5 w-3.5" />
          </IconButton>
          <IconButton
            size="sm"
            label={t('tenant_routing.pool.remove_target')}
            disabled={deleting}
            onClick={() => onDelete(target)}
            className="text-bad hover:text-bad"
            data-testid={`tenant-routing-target-delete-${target.id}`}
          >
            <Trash2 aria-hidden="true" className="h-3.5 w-3.5" />
          </IconButton>
        </span>
      </Td>
    </Tr>
  );
}

/**
 * 目标停用 plus the operator's removal note (0082): the hover says
 * 「手动摘除 · 备注 · 谁 · 多久前」, the note itself sits beside the chip
 * (truncated) and opens the target editor, where it can be changed.
 */
export function DisabledTargetChip({
  target,
  onEdit,
  nowMs = Date.now(),
}: {
  readonly target: RoutingSkuTargetRecord;
  readonly onEdit: (target: RoutingSkuTargetRecord) => void;
  readonly nowMs?: number;
}): JSX.Element {
  const t = useT();
  const tip = t('tenant_routing.disabled_note.tip', { detail: disabledDetail(target, nowMs, t) });
  const note = target.disabled_note ?? '';
  return (
    <>
      <Tooltip content={tip}>
        <span title={tip} data-testid={`tenant-routing-disabled-chip-${target.id}`}>
          <Chip tone="neutral">{t('tenant_routing.status.target_disabled')}</Chip>
        </span>
      </Tooltip>
      <button
        type="button"
        className="max-w-[140px] truncate text-left text-micro text-paper-low underline decoration-line hover:text-paper-mid"
        title={t('tenant_routing.disabled_note.edit')}
        aria-label={t('tenant_routing.disabled_note.edit')}
        onClick={() => onEdit(target)}
        data-testid={`tenant-routing-disabled-note-${target.id}`}
      >
        {note.length > 0 ? note : t('tenant_routing.disabled_note.none')}
      </button>
    </>
  );
}
